import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import { host } from "../../config/Config";
import { EditCalendar } from "@mui/icons-material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

export default function StepperAppointments() {
    const [activeStep, setActiveStep] = React.useState(0);
    const [visibleButtons, setVisibleButtons] = React.useState({});
    const [appointments, setAppointments] = React.useState({});
    const [openDialog, setOpenDialog] = React.useState(false);
    const [selectedAppointment, setSelectedAppointment] = React.useState(null);

    async function sendCancellationToBackend(appointmentDateTimeList) {
        const url = `${host}/api/v1/reservations/admin/cancel`; // Different API for cancellation
        const token = localStorage.getItem("jwt-vlada");

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token.trim()}`,
                },
                body: JSON.stringify({
                    appointmentDateTimeList: appointmentDateTimeList,
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    `Failed to cancel appointments: ${error.message}`
                );
            }
        } catch (error) {}
    }

    async function sendAppointmentsToBackend(appointmentDateTimeList) {
        const url = `${host}/api/v1/reservations/admin/whole-day`; // Adjust the URL as needed
        const token = localStorage.getItem("jwt-vlada");

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token.trim()}`,
                },
                body: JSON.stringify({
                    appointmentDateTimeList: appointmentDateTimeList,
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    `Failed to update appointments: ${error.message}`
                );
            }

            const data = await response.json();
        } catch (error) {}
    }

    function updateAppointmentsForDate(selectedDate) {
        if (!selectedDate || !appointments[selectedDate]) return;

        const appointmentDateTimeList = [];

        const updatedAppointments = appointments[selectedDate].map(
            (appointment) => {
                if (!appointment.client) {
                    // Only modify available appointments
                    const startTime = appointment.time
                        .split(" - ")[0]
                        .replace(".", ":"); // Extract and format the start time
                    const formattedDate = selectedDate
                        .split(".")
                        .reverse()
                        .join("-"); // Convert dd.MM.yyyy -> YYYY-MM-DD
                    const fullStartTime = `${formattedDate} ${startTime}:00`; // Combine date and time for full datetime

                    // Add start time to the list
                    appointmentDateTimeList.push(fullStartTime);

                    return {
                        ...appointment,
                        haircut: "admin", // Set the "haircut" field to "admin"
                        phone: "admin", // Set the "phone" field to "admin"
                        client: "admin", // Set the "client" field to "admin"
                        isOccupied: true,
                    };
                }
                return appointment; // Return unchanged appointment if already occupied
            }
        );

        // Update the appointments state with the modified appointments for the selected date
        setAppointments((prevAppointments) => ({
            ...prevAppointments,
            [selectedDate]: updatedAppointments,
        }));

        sendAppointmentsToBackend(appointmentDateTimeList);
    }

    function getAvailableAppointmentsByDate(selectedDate) {
        if (!selectedDate || !appointments[selectedDate]) return [];

        return appointments[selectedDate]
            .filter((appointment) => !appointment.client) // Only extract appointments where client is null
            .map((appointment) => {
                const formattedDate = selectedDate
                    .split(".")
                    .reverse()
                    .join("-"); // Convert dd.MM.yyyy -> YYYY-MM-DD
                const startTime = appointment.time
                    .split(" - ")[0]
                    .replace(".", ":"); // Extract start time and format HH:mm
                return `${formattedDate} ${startTime}:00`; // Final format YYYY-MM-DD HH:mm:ss
            });
    }

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchAppointments();
                setAppointments(data);
            } catch (error) {
                console.error("Error fetching appointments:", error.message);
            }
        };

        fetchData();
    }, []);

    async function fetchAppointments() {
        const url = `${host}/api/v1/appointments/admin`;
        const token = localStorage.getItem("jwt-vlada");

        try {
            const response = await fetch(`${url}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token.trim()}`,
                },
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(
                    `Failed to fetch appointments: ${error.message}`
                );
            }

            const data = await response.json();

            // Transform the backend response into the desired format
            const apps = Object.entries(data).reduce(
                (acc, [date, appointmentList]) => {
                    acc[date] = appointmentList.map((appointment) => {
                        const transformedAppointment = {
                            time: appointment.time,
                            haircut: appointment.haircut || null,
                            client: appointment.client || null,
                            phone: appointment.phone || null,
                            isOccupied: appointment.isOccupied || false,
                        };
                        return transformedAppointment;
                    });
                    return acc;
                },
                {}
            );

            return apps;
        } catch (error) {
            console.error("Error fetching appointments:", error.message);
            throw error;
        }
    }
    // Get sorted dates from appointments
    const appointmentsByDate = Object.keys(appointments)
        .sort((a, b) => {
            const today = new Date();
            const dateA = new Date(a.split(".").reverse().join("-")); // Convert dd.MM.yyyy to yyyy-MM-dd
            const dateB = new Date(b.split(".").reverse().join("-"));
            return dateA - dateB; // Sort in ascending order
        })
        .filter((date) => {
            const today = new Date();
            today.setHours(0, 0, 1);
            const parsedDate = new Date(date.split(".").reverse().join("-"));
            return parsedDate >= today;
        });

    const handleStepClick = (stepIndex) => {
        setActiveStep((prevStep) => (prevStep === stepIndex ? -1 : stepIndex));
    };

    const toggleButtonVisibility = (step, i) => {
        const key = `${step}-${i}`;
        setVisibleButtons((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    return (
        <Box
            sx={{ maxWidth: 400 }}
            className='barber-zajednica-admin-appointments-stepper'
        >
            <Stepper activeStep={activeStep} orientation='vertical'>
                {appointmentsByDate.map((step, index) => (
                    <Step key={step}>
                        <StepLabel
                            className='barber-zajednica-appointments-stepper-label'
                            onClick={() => handleStepClick(index)}
                            icon={<EditCalendar style={{ color: "#be9455" }} />}
                        >
                            Termin - {step}
                        </StepLabel>
                        <StepContent>
                            <div className='barber-zajednica-appointments-content'>
                                {appointments[step].map((item, i) => {
                                    const isVisible =
                                        visibleButtons[`${step}-${i}`];
                                    return (
                                        <React.Fragment key={i}>
                                            <div
                                                className={
                                                    item.haircut &&
                                                    !item.isOccupied
                                                        ? "barber-zajednica-appointments-content-item-filled"
                                                        : item.isOccupied
                                                        ? "zauzet-termin"
                                                        : "barber-zajednica-appointments-content-item"
                                                }
                                                style={{ marginBottom: "15px" }}
                                            >
                                                {item.haircut &&
                                                !item.isOccupied ? (
                                                    <div>
                                                        <div
                                                            className='barber-zajednica-appointments-content-item-filled-item'
                                                            onClick={() =>
                                                                toggleButtonVisibility(
                                                                    step,
                                                                    i
                                                                )
                                                            }
                                                            style={{
                                                                color: "black",
                                                            }}
                                                        >
                                                            {item.time}
                                                        </div>

                                                        {item.haircut
                                                            .split("#")
                                                            .map(
                                                                (
                                                                    haircut,
                                                                    index
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            index
                                                                        }
                                                                        className='barber-zajednica-appointments-content-item-filled-item'
                                                                        onClick={() =>
                                                                            toggleButtonVisibility(
                                                                                step,
                                                                                i
                                                                            )
                                                                        }
                                                                    >
                                                                        {
                                                                            haircut.split(
                                                                                "-"
                                                                            )[0]
                                                                        }
                                                                    </div>
                                                                )
                                                            )}
                                                        <div>
                                                            <div
                                                                className='barber-zajednica-appointments-content-item-filled-item'
                                                                style={{
                                                                    color: "black",
                                                                }}
                                                            >
                                                                Ukupna cena:{" "}
                                                                {item.haircut
                                                                    .split("#")
                                                                    .filter(
                                                                        (
                                                                            haircut
                                                                        ) =>
                                                                            haircut.trim() !==
                                                                            ""
                                                                    )
                                                                    .map(
                                                                        (
                                                                            haircut
                                                                        ) => {
                                                                            const parts =
                                                                                haircut.split(
                                                                                    "-"
                                                                                );
                                                                            return parts[1]
                                                                                ? parseFloat(
                                                                                      parts[1]
                                                                                          .replace(
                                                                                              "RSD",
                                                                                              ""
                                                                                          )
                                                                                          .trim()
                                                                                  )
                                                                                : 0;
                                                                        }
                                                                    )
                                                                    .reduce(
                                                                        (
                                                                            total,
                                                                            price
                                                                        ) =>
                                                                            total +
                                                                            price,
                                                                        0
                                                                    )}{" "}
                                                                RSD
                                                            </div>
                                                        </div>
                                                        <div
                                                            className='barber-zajednica-appointments-content-item-filled-item'
                                                            onClick={() =>
                                                                toggleButtonVisibility(
                                                                    step,
                                                                    i
                                                                )
                                                            }
                                                        >
                                                            {item.client}
                                                        </div>
                                                        <div className='barber-zajednica-appointments-content-item-filled-item'>
                                                            <a
                                                                href={`tel:${item.phone}`}
                                                            >
                                                                {item.phone}
                                                            </a>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div
                                                        onClick={() =>
                                                            toggleButtonVisibility(
                                                                step,
                                                                i
                                                            )
                                                        }
                                                    >
                                                        {item.time}
                                                    </div>
                                                )}
                                            </div>

                                            {isVisible && (
                                                <Button
                                                    className={
                                                        item.isOccupied
                                                            ? "spec-dugme-opoziv"
                                                            : "spec-dugme-otkaz"
                                                    }
                                                    variant='contained'
                                                    sx={{
                                                        marginBottom: "10px",
                                                    }}
                                                    onClick={(e) => {
                                                        toggleButtonVisibility(
                                                            step,
                                                            i
                                                        );
                                                        e.stopPropagation();

                                                        const selectedDate =
                                                            step; // The date of the current step
                                                        const selectedTime =
                                                            item.time
                                                                .split(" - ")[0]
                                                                .replace(
                                                                    ".",
                                                                    ":"
                                                                ); // Format time
                                                        const formattedDate = `${selectedDate
                                                            .split(".")
                                                            .reverse()
                                                            .join(
                                                                "-"
                                                            )} ${selectedTime}:00`;

                                                        if (!item.isOccupied) {
                                                            // Extract and format the date correctly

                                                            // Check if client is null
                                                            if (
                                                                item.client ===
                                                                null
                                                            ) {
                                                                const updatedAppointments =
                                                                    appointments[
                                                                        selectedDate
                                                                    ].map(
                                                                        (
                                                                            appointment
                                                                        ) =>
                                                                            appointment.time ===
                                                                            item.time
                                                                                ? {
                                                                                      ...appointment,
                                                                                      client: "admin",
                                                                                      haircut:
                                                                                          "admin",
                                                                                      phone: "admin",
                                                                                      isOccupied: true,
                                                                                  }
                                                                                : appointment
                                                                    );

                                                                // Update state
                                                                setAppointments(
                                                                    (
                                                                        prevAppointments
                                                                    ) => ({
                                                                        ...prevAppointments,
                                                                        [selectedDate]:
                                                                            updatedAppointments,
                                                                    })
                                                                );

                                                                // Send to backend
                                                                sendAppointmentsToBackend(
                                                                    [
                                                                        formattedDate,
                                                                    ]
                                                                );
                                                            } else {
                                                                const updatedAppointments =
                                                                    appointments[
                                                                        selectedDate
                                                                    ].map(
                                                                        (
                                                                            appointment
                                                                        ) =>
                                                                            appointment.time ===
                                                                            item.time
                                                                                ? {
                                                                                      ...appointment,
                                                                                      client: null,
                                                                                      haircut:
                                                                                          null,
                                                                                      phone: null,
                                                                                      isOccupied: false,
                                                                                  }
                                                                                : appointment
                                                                    );

                                                                setSelectedAppointment(
                                                                    item
                                                                );
                                                                setOpenDialog(
                                                                    true
                                                                );

                                                                // // Update state
                                                                // setAppointments((prevAppointments) => ({
                                                                //   ...prevAppointments,
                                                                //   [selectedDate]: updatedAppointments,
                                                                // }));

                                                                // // Send cancellation request to backend
                                                                // // sendCancellationToBackend([formattedDate]);
                                                                // setOpenDialog(true);
                                                            }
                                                        } else {
                                                            const updatedAppointments =
                                                                appointments[
                                                                    selectedDate
                                                                ].map(
                                                                    (
                                                                        appointment
                                                                    ) =>
                                                                        appointment.time ===
                                                                        item.time
                                                                            ? {
                                                                                  ...appointment,
                                                                                  client: null,
                                                                                  haircut:
                                                                                      null,
                                                                                  phone: null,
                                                                                  isOccupied: false,
                                                                              }
                                                                            : appointment
                                                                );

                                                            // Update state
                                                            setAppointments(
                                                                (
                                                                    prevAppointments
                                                                ) => ({
                                                                    ...prevAppointments,
                                                                    [selectedDate]:
                                                                        updatedAppointments,
                                                                })
                                                            );

                                                            // Send cancellation request to backend
                                                            sendCancellationToBackend(
                                                                [formattedDate]
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {item.haircut
                                                        ? "Oslobodi Termin"
                                                        : item.isOccupied
                                                        ? "Oslobodi termin"
                                                        : "Zauzmi termin"}
                                                </Button>
                                            )}
                                            <Dialog
                                                open={openDialog}
                                                onClose={() =>
                                                    setOpenDialog(false)
                                                }
                                            >
                                                <DialogTitle>
                                                    Potvrda
                                                </DialogTitle>
                                                <DialogContent>
                                                    <p>
                                                        Da li ste sigurni da
                                                        želite da oslobodite
                                                        termin za{" "}
                                                        {
                                                            selectedAppointment?.time
                                                        }
                                                        ?
                                                    </p>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button
                                                        onClick={() =>
                                                            setOpenDialog(false)
                                                        }
                                                        color='primary'
                                                    >
                                                        Otkazati
                                                    </Button>
                                                    <Button
                                                        onClick={() => {
                                                            setOpenDialog(
                                                                false
                                                            );

                                                            const selectedDate =
                                                                appointmentsByDate[
                                                                    activeStep
                                                                ]; // The date of the current step
                                                            const selectedTime =
                                                                selectedAppointment.time
                                                                    .split(
                                                                        " - "
                                                                    )[0]
                                                                    .replace(
                                                                        ".",
                                                                        ":"
                                                                    );
                                                            const formattedDate = `${selectedDate
                                                                .split(".")
                                                                .reverse()
                                                                .join(
                                                                    "-"
                                                                )} ${selectedTime}:00`;

                                                            // Free the selected appointment
                                                            const updatedAppointments =
                                                                appointments[
                                                                    selectedDate
                                                                ].map(
                                                                    (
                                                                        appointment
                                                                    ) =>
                                                                        appointment.time ===
                                                                        selectedAppointment.time
                                                                            ? {
                                                                                  ...appointment,
                                                                                  client: null,
                                                                                  haircut:
                                                                                      null,
                                                                                  phone: null,
                                                                                  isOccupied: false,
                                                                              }
                                                                            : appointment
                                                                );

                                                            setAppointments(
                                                                (
                                                                    prevAppointments
                                                                ) => ({
                                                                    ...prevAppointments,
                                                                    [selectedDate]:
                                                                        updatedAppointments,
                                                                })
                                                            );

                                                            // Send the cancellation request to the backend
                                                            sendCancellationToBackend(
                                                                [formattedDate]
                                                            );
                                                        }}
                                                        color='secondary'
                                                    >
                                                        Potvrdi
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                            <Button
                                className='spec-dugme-otkaz'
                                variant='contained'
                                sx={{
                                    marginTop: "40px",
                                    marginBottom: "-15px",
                                    height: "50px",
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();

                                    const selectedDate =
                                        appointmentsByDate[activeStep];
                                    const availableAppointments =
                                        getAvailableAppointmentsByDate(
                                            selectedDate
                                        );

                                    updateAppointmentsForDate(selectedDate);
                                }}
                            >
                                Rezerviši Ceo Dan
                            </Button>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
}
